/**
 * Pooled particle system (DESIGN 6.2: 2000 particles, per-element emitters;
 * 6.4 particle styles). Struct-of-arrays, fixed size, no allocation during
 * play. Particles live in world units with a height `z` so droplets fall
 * and embers rise in the 2.5D view.
 *
 * Drawing batches by (kind, colour, alpha step): one fillStyle change and one
 * fill()/stroke() per bucket, so a full pool costs a few hundred state
 * changes instead of thousands. Magic is drawn with 'lighter'; rock chunks
 * are opaque.
 */

import { ALL_ELEMENTS, CLIENT, type Element } from '@shared/index.js';
import { ELEMENT_SWATCH, rgba } from '../palette.js';
import { TAU, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';

export const PK = {
  dot: 0,
  spark: 1,
  flame: 2,
  chunk: 3,
  puff: 4,
  shard: 5,
  droplet: 6,
  streak: 7,
  sparkle: 8,
} as const;
export type ParticleKind = (typeof PK)[keyof typeof PK];

const KINDS = 9;
const MAX_COLORS = 64;
const ALPHA_STEPS = 4;
const ALPHA_VALUES = [0.25, 0.5, 0.75, 1] as const;
const BUCKETS = KINDS * MAX_COLORS * ALPHA_STEPS;
/** Largest single emit so one event cannot flush the pool. */
const MAX_EMIT = 160;

/** Fixed colour slots, registered right after the elements (which occupy 0..n-1 in ALL_ELEMENTS order). */
const N_EL = ALL_ELEMENTS.length;
export const PC = { white: N_EL, grey: N_EL + 1, red: N_EL + 2, gold: N_EL + 3, blood: N_EL + 4 } as const;
const WATER_COL = ALL_ELEMENTS.indexOf('water');

export class Particles {
  readonly max: number = CLIENT.particlePool;
  count = 0;
  /** Share of emitted particles kept (1 on desktop; phones keep fewer, and the pool fills more slowly). */
  budget = 1;

  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly z: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly vz: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly size: Float32Array;
  private readonly rot: Float32Array;
  private readonly spin: Float32Array;
  private readonly grav: Float32Array;
  private readonly drag: Float32Array;
  private readonly kind: Uint8Array;
  private readonly col: Uint8Array;

  private readonly head: Int32Array;
  private readonly next: Int32Array;
  private readonly colors: string[] = [];
  private readonly colorAlpha: string[][] = [];
  private readonly colorIndexOf = new Map<string, number>();
  private rr = 0;
  private seed = 12345;

  constructor() {
    const n = this.max;
    this.x = new Float32Array(n); this.y = new Float32Array(n); this.z = new Float32Array(n);
    this.vx = new Float32Array(n); this.vy = new Float32Array(n); this.vz = new Float32Array(n);
    this.life = new Float32Array(n); this.maxLife = new Float32Array(n); this.size = new Float32Array(n);
    this.rot = new Float32Array(n); this.spin = new Float32Array(n);
    this.grav = new Float32Array(n); this.drag = new Float32Array(n);
    this.kind = new Uint8Array(n); this.col = new Uint8Array(n);
    this.head = new Int32Array(BUCKETS);
    this.next = new Int32Array(n);
    for (const el of ALL_ELEMENTS) this.registerColor(ELEMENT_SWATCH[el].core);
    this.registerColor('#FFFFFF');
    this.registerColor('#9AA0A6');
    this.registerColor('#FF3B3B');
    this.registerColor('#F5E663');
    this.registerColor('#8B1A1A');
  }

  /** Colour slot for a hex colour (registers up to MAX_COLORS; falls back to white). */
  colorIndex(hex: string): number {
    const hit = this.colorIndexOf.get(hex);
    if (hit !== undefined) return hit;
    if (this.colors.length >= MAX_COLORS) return PC.white;
    return this.registerColor(hex);
  }

  private registerColor(hex: string): number {
    const i = this.colors.length;
    this.colors.push(hex);
    this.colorAlpha.push(ALPHA_VALUES.map((a) => rgba(hex, a)));
    this.colorIndexOf.set(hex, i);
    return i;
  }

  /** Cheap deterministic-per-session random in [0,1). */
  private rnd(): number {
    this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  clear(): void { this.count = 0; }

  // ---------------------------------------------------------------- low level

  spawn(kind: ParticleKind, col: number, x: number, y: number, z: number, vx: number, vy: number, vz: number,
    life: number, size: number, grav = 0, drag = 1, spin = 0): void {
    if (this.budget < 1 && this.rnd() >= this.budget) return;
    let i: number;
    if (this.count < this.max) i = this.count++;
    else { i = this.rr++ % this.max; }
    this.kind[i] = kind; this.col[i] = col;
    this.x[i] = x; this.y[i] = y; this.z[i] = z;
    this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.life[i] = life; this.maxLife[i] = life; this.size[i] = size;
    this.rot[i] = this.rnd() * TAU; this.spin[i] = spin;
    this.grav[i] = grav; this.drag[i] = drag;
  }

  // ---------------------------------------------------------------- per-element emitters (DESIGN 6.3 particle column)

  /** Radial burst of an element's native particle at (x, y, z). */
  burst(el: Element, x: number, y: number, z: number, n: number, speed = 4): void {
    n = Math.min(n, MAX_EMIT);
    const col = ALL_ELEMENTS.indexOf(el);
    for (let k = 0; k < n; k++) {
      const a = this.rnd() * TAU;
      const sp = speed * (0.4 + this.rnd() * 0.8);
      const vx = Math.cos(a) * sp, vy = Math.sin(a) * sp;
      this.spawnElement(el, col, x, y, z, vx, vy);
    }
  }

  private spawnElement(el: Element, col: number, x: number, y: number, z: number, vx: number, vy: number): void {
    const r = this.rnd();
    switch (el) {
      case 'water': this.spawn(PK.droplet, col, x, y, z + 0.2, vx, vy, 2 + r * 3, 0.5 + r * 0.3, 3, 9, 0.9); return;
      case 'life': this.spawn(PK.sparkle, col, x, y, z, vx * 0.3, vy * 0.3, 0.8 + r, 0.9 + r * 0.4, 4 + r * 3, 0, 0.9); return;
      case 'shield': this.spawn(PK.dot, col, x, y, z + 0.3, vx * 0.5, vy * 0.5, 0.3, 0.5 + r * 0.3, 3 + r * 2, 0, 0.85); return;
      case 'cold': this.spawn(PK.dot, PC.white, x, y, z + 0.3, vx * 0.4, vy * 0.4, 0.4 + r * 0.6, 0.8 + r * 0.5, 2 + r * 2, -0.5, 0.9); return;
      case 'lightning': this.spawn(PK.spark, PC.white, x, y, z, vx * 2, vy * 2, (r - 0.5) * 3, 0.12 + r * 0.1, 8 + r * 8, 0, 0.8); return;
      case 'arcane': this.spawn(PK.dot, col, x, y, z, vx, vy, (r - 0.3) * 2, 0.35 + r * 0.3, 4 + r * 3, 0, 0.85, 4); return;
      case 'earth': this.spawn(PK.chunk, col, x, y, z + 0.1, vx, vy, 3 + r * 4, 0.5 + r * 0.5, 3 + r * 4, 9, 0.95, 6 + r * 6); return;
      case 'fire': this.spawn(PK.flame, col, x, y, z, vx * 0.6, vy * 0.6, 1.5 + r * 1.5, 0.3 + r * 0.3, 6 + r * 6, -1, 0.9); return;
      case 'steam': this.spawn(PK.puff, col, x, y, z, vx * 0.5, vy * 0.5, 0.8 + r * 0.6, 0.6 + r * 0.5, 6 + r * 6, -0.3, 0.9); return;
      case 'ice': this.spawn(PK.shard, col, x, y, z + 0.2, vx, vy, 2 + r * 3, 0.4 + r * 0.3, 5 + r * 4, 8, 0.92, 8); return;
      // Mud: heavy brown clumps that arc and drop; Lava: molten globs that glow and fall; Spring: bright droplets that sparkle.
      case 'mud': this.spawn(PK.droplet, col, x, y, z + 0.2, vx * 0.8, vy * 0.8, 1.5 + r * 2, 0.5 + r * 0.3, 4 + r * 4, 7, 0.9); return;
      case 'lava': if (r < 0.5) this.spawn(PK.flame, col, x, y, z, vx * 0.6, vy * 0.6, 1 + r * 1.5, 0.4 + r * 0.3, 6 + r * 6, -0.6, 0.9); else this.spawn(PK.chunk, col, x, y, z + 0.1, vx, vy, 2 + r * 3, 0.6 + r * 0.4, 3 + r * 4, 7, 0.94, 6); return;
      case 'spring': if (r < 0.65) this.spawn(PK.droplet, col, x, y, z + 0.2, vx, vy, 1.5 + r * 2, 0.45 + r * 0.25, 3 + r * 2.5, 4, 0.93); else this.spawn(PK.sparkle, col, x, y, z, vx * 0.4, vy * 0.4, 0.8 + r, 0.7 + r * 0.4, 4 + r * 3, 0, 0.9); return;
    }
  }

  /**
   * A spray's stream (DESIGN 6.4, sprayCone.ts): each particle leaves the staff at height `z`, flies straight and
   * lands on the ground inside the cone (within `half` of `ang`, out to `range`, more of them toward the far part)
   * just as it dies, so the stream reaches exactly as far as the spray hits: no drag, no gravity to fall short.
   */
  sprayCone(el: Element, x: number, y: number, z: number, ang: number, half: number, range: number, n: number): void {
    n = Math.min(n, MAX_EMIT);
    const col = ALL_ELEMENTS.indexOf(el);
    const out = 0.3;
    for (let k = 0; k < n; k++) {
      const a = ang + (this.rnd() * 2 - 1) * half;
      const d = Math.max(0, range * (0.3 + 0.7 * Math.sqrt(this.rnd())) - out);
      const r = this.rnd();
      const life = 0.3 + r * 0.14;
      const c = Math.cos(a), s = Math.sin(a);
      const x0 = x + c * out, y0 = y + s * out;
      const vx = (c * d) / life, vy = (s * d) / life, vz = -z / life;
      switch (el) {
        case 'fire': this.spawn(PK.flame, col, x0, y0, z, vx, vy, vz, life, 5 + r * 7, 0, 1); break;
        case 'water': case 'mud': case 'spring': this.spawn(PK.droplet, col, x0, y0, z, vx, vy, vz, life, 2.5 + r * 2.5, 0, 1); break;
        case 'cold': this.spawn(PK.dot, PC.white, x0, y0, z, vx, vy, vz, life, 1.8 + r * 2.2, 0, 1); break;
        case 'steam': this.spawn(PK.puff, col, x0, y0, z, vx, vy, vz, life, 6 + r * 7, 0, 1); break;
        case 'lightning': this.spawn(PK.spark, PC.white, x0, y0, z, vx, vy, vz, life, 8 + r * 6, 0, 1); break;
        default: this.spawn(PK.dot, col, x0, y0, z, vx, vy, vz, life, 3 + r * 3, 0, 1); break;
      }
    }
  }

  /** Rising particles along a storm segment (DESIGN 6.4 "animated band"). */
  stormBand(el: Element, x0: number, y0: number, x1: number, y1: number, thickness: number, n: number): void {
    n = Math.min(n, MAX_EMIT);
    const col = ALL_ELEMENTS.indexOf(el);
    const dx = x1 - x0, dy = y1 - y0;
    const l = Math.hypot(dx, dy) || 1;
    const nx = -dy / l, ny = dx / l;
    for (let k = 0; k < n; k++) {
      const t = this.rnd();
      const off = (this.rnd() - 0.5) * thickness;
      const px = x0 + dx * t + nx * off, py = y0 + dy * t + ny * off;
      const r = this.rnd();
      switch (el) {
        case 'fire': this.spawn(PK.flame, col, px, py, 0, 0, 0, 1 + r, 0.5 + r * 0.3, 4 + r * 4, -0.8, 0.95); break;
        case 'lightning': this.spawn(PK.spark, col, px, py, r * 0.6, (r - 0.5) * 2, (this.rnd() - 0.5) * 2, 1.5, 0.12, 6 + r * 6, 0, 0.9); break;
        case 'cold': this.spawn(PK.dot, PC.white, px, py, 0.2 + r * 0.6, 0, 0, 0.6, 0.8 + r * 0.4, 1.5 + r, -0.4, 0.98); break;
        case 'water': this.spawn(PK.droplet, col, px, py, 0.1, 0, 0, 1.2 + r, 0.5 + r * 0.3, 2 + r, 2, 0.98); break;
        case 'steam': this.spawn(PK.puff, col, px, py, 0, 0, 0, 0.6 + r * 0.4, 0.7 + r * 0.4, 5 + r * 5, -0.3, 0.98); break;
        default: this.spawnElement(el, col, px, py, 0, 0, 0); break;
      }
    }
  }

  /** One wet drip falling off a body (DESIGN 6.3 "wet = drip overlay + droplets"). */
  drip(x: number, y: number, z: number): void {
    const a = this.rnd() * TAU;
    this.spawn(PK.droplet, WATER_COL, x + Math.cos(a) * 0.3, y + Math.sin(a) * 0.15, z, 0, 0, 0, 0.45, 2.5, 9, 1);
  }

  /** Embers rising off a burning body; `n` scales with stacks. */
  embers(x: number, y: number, z: number, n: number): void {
    const col = ALL_ELEMENTS.indexOf('fire');
    for (let k = 0; k < n; k++) {
      const a = this.rnd() * TAU;
      const r = this.rnd();
      this.spawn(PK.flame, col, x + Math.cos(a) * 0.3, y + Math.sin(a) * 0.15, z + r * 0.5, (r - 0.5) * 0.6, (this.rnd() - 0.5) * 0.3, 1.2 + r, 0.3 + r * 0.25, 3 + r * 4, -1, 0.92);
    }
  }

  /** Frost specks drifting off a chilled body. */
  frost(x: number, y: number, z: number, n: number): void {
    for (let k = 0; k < n; k++) {
      const a = this.rnd() * TAU;
      this.spawn(PK.dot, PC.white, x + Math.cos(a) * 0.4, y + Math.sin(a) * 0.2, z + this.rnd() * 0.8, (this.rnd() - 0.5) * 0.4, (this.rnd() - 0.5) * 0.4, 0.3, 0.6 + this.rnd() * 0.4, 1.5, -0.2, 0.95);
    }
  }

  /** Purple sparks off a shocked body. */
  sparks(x: number, y: number, z: number, n: number): void {
    const col = ALL_ELEMENTS.indexOf('lightning');
    for (let k = 0; k < n; k++) {
      const a = this.rnd() * TAU;
      const s = 3 + this.rnd() * 4;
      this.spawn(PK.spark, k % 2 === 0 ? col : PC.white, x, y, z + this.rnd() * 0.6, Math.cos(a) * s, Math.sin(a) * s, (this.rnd() - 0.5) * 4, 0.1 + this.rnd() * 0.1, 5 + this.rnd() * 5, 0, 0.8);
    }
  }

  /** Grey dust puffs (knockdown, deaths, quake). */
  dust(x: number, y: number, n: number, speed = 2): void {
    for (let k = 0; k < n; k++) {
      const a = this.rnd() * TAU;
      const s = speed * (0.3 + this.rnd() * 0.7);
      this.spawn(PK.puff, PC.grey, x, y, 0.05, Math.cos(a) * s, Math.sin(a) * s * 0.5, 0.3 + this.rnd() * 0.5, 0.5 + this.rnd() * 0.4, 5 + this.rnd() * 5, -0.2, 0.9);
    }
  }

  /** Rock / plate chunks in an arbitrary colour (orc armour, enemy deaths, wall crumble). */
  chunks(hex: string, x: number, y: number, z: number, n: number, speed = 4): void {
    const col = this.colorIndex(hex);
    for (let k = 0; k < Math.min(n, MAX_EMIT); k++) {
      const a = this.rnd() * TAU;
      const s = speed * (0.4 + this.rnd() * 0.8);
      this.spawn(PK.chunk, col, x, y, z, Math.cos(a) * s, Math.sin(a) * s, 2 + this.rnd() * 5, 0.5 + this.rnd() * 0.5, 2.5 + this.rnd() * 3, 9, 0.95, 5 + this.rnd() * 8);
    }
  }

  /** Short streaks behind a hasted body or in a wind blast. */
  streaks(col: number, x: number, y: number, z: number, ang: number, n: number, speed: number): void {
    for (let k = 0; k < n; k++) {
      const a = ang + (this.rnd() - 0.5) * 0.6;
      const s = speed * (0.6 + this.rnd() * 0.6);
      this.spawn(PK.streak, col, x, y, z + this.rnd() * 0.6, Math.cos(a) * s, Math.sin(a) * s, 0, 0.2 + this.rnd() * 0.15, 8 + this.rnd() * 8, 0, 0.85);
    }
  }

  // ---------------------------------------------------------------- simulation

  step(dt: number): void {
    const { x, y, z, vx, vy, vz, life, grav, drag, rot, spin, kind } = this;
    let i = 0;
    while (i < this.count) {
      const l = life[i]! - dt;
      if (l <= 0) { this.swapRemove(i); continue; }
      life[i] = l;
      const d = Math.pow(drag[i]!, dt * 60);
      vx[i] = vx[i]! * d;
      vy[i] = vy[i]! * d;
      vz[i] = vz[i]! - grav[i]! * dt;
      x[i] = x[i]! + vx[i]! * dt;
      y[i] = y[i]! + vy[i]! * dt;
      let h = z[i]! + vz[i]! * dt;
      if (h < 0) {
        h = 0;
        const k = kind[i];
        if (k === PK.chunk || k === PK.shard) vz[i] = -vz[i]! * 0.35; // bounce once-ish
        else if (k === PK.droplet) { life[i] = Math.min(l, 0.08); vz[i] = 0; } // splash
        else vz[i] = 0;
      }
      z[i] = h;
      rot[i] = rot[i]! + spin[i]! * dt;
      i++;
    }
  }

  private swapRemove(i: number): void {
    const j = --this.count;
    if (i === j) return;
    this.x[i] = this.x[j]!; this.y[i] = this.y[j]!; this.z[i] = this.z[j]!;
    this.vx[i] = this.vx[j]!; this.vy[i] = this.vy[j]!; this.vz[i] = this.vz[j]!;
    this.life[i] = this.life[j]!; this.maxLife[i] = this.maxLife[j]!; this.size[i] = this.size[j]!;
    this.rot[i] = this.rot[j]!; this.spin[i] = this.spin[j]!;
    this.grav[i] = this.grav[j]!; this.drag[i] = this.drag[j]!;
    this.kind[i] = this.kind[j]!; this.col[i] = this.col[j]!;
  }

  // ---------------------------------------------------------------- drawing

  draw(ctx: Ctx, cam: Camera): void {
    const head = this.head;
    head.fill(-1);
    const { kind, col, life, maxLife, next } = this;
    for (let i = 0; i < this.count; i++) {
      const f = life[i]! / maxLife[i]!;
      const k = kind[i]!;
      const alpha = k === PK.puff ? f * 0.55 : f;
      let a = Math.ceil(alpha * ALPHA_STEPS) - 1;
      if (a < 0) a = 0; else if (a >= ALPHA_STEPS) a = ALPHA_STEPS - 1;
      const b = (k * MAX_COLORS + col[i]!) * ALPHA_STEPS + a;
      next[i] = head[b]!;
      head[b] = i;
    }
    ctx.lineCap = 'round';
    // Opaque chunks first, then additive magic.
    ctx.globalCompositeOperation = 'source-over';
    this.drawKind(ctx, cam, PK.chunk);
    ctx.globalCompositeOperation = 'lighter';
    for (let k = 0; k < KINDS; k++) if (k !== PK.chunk) this.drawKind(ctx, cam, k as ParticleKind);
    ctx.globalCompositeOperation = 'source-over';
  }

  private drawKind(ctx: Ctx, cam: Camera, k: ParticleKind): void {
    const { head, next, x, y, z, vx, vy, life, maxLife, size, rot } = this;
    for (let c = 0; c < this.colors.length; c++) {
      for (let a = 0; a < ALPHA_STEPS; a++) {
        let i = head[(k * MAX_COLORS + c) * ALPHA_STEPS + a]!;
        if (i < 0) continue;
        const style = this.colorAlpha[c]![a]!;
        const stroked = k === PK.spark || k === PK.streak;
        if (stroked) { ctx.strokeStyle = style; ctx.lineWidth = k === PK.spark ? 1.5 : 2; }
        else ctx.fillStyle = style;
        ctx.beginPath();
        while (i >= 0) {
          const px = cam.sx(x[i]!);
          const py = cam.sy(y[i]!) - z[i]! * UNIT;
          const f = life[i]! / maxLife[i]!;
          const s = size[i]!;
          switch (k) {
            case PK.dot: {
              const r = s * 0.5;
              ctx.moveTo(px + r, py);
              ctx.arc(px, py, r, 0, TAU);
              break;
            }
            case PK.droplet: {
              const r = z[i]! > 0 ? s * 0.5 : s; // splash flattens
              ctx.moveTo(px + r, py);
              ctx.ellipse(px, py, r, z[i]! > 0 ? r * 1.3 : r * 0.4, 0, 0, TAU);
              break;
            }
            case PK.spark:
            case PK.streak: {
              // A line of `size` px trailing opposite the velocity.
              const sp = Math.hypot(vx[i]!, vy[i]!) || 1;
              ctx.moveTo(px, py);
              ctx.lineTo(px - (vx[i]! / sp) * s, py - (vy[i]! / sp) * s);
              break;
            }
            case PK.flame: {
              const h = s * (0.4 + f * 0.9);
              const w = s * 0.5 * f + 1;
              ctx.moveTo(px, py - h);
              ctx.lineTo(px + w, py + h * 0.2);
              ctx.lineTo(px - w, py + h * 0.2);
              ctx.closePath();
              break;
            }
            case PK.chunk:
            case PK.shard: {
              const r = rot[i]!;
              const cr = Math.cos(r), sr = Math.sin(r);
              const w = k === PK.shard ? s * 0.35 : s * 0.6;
              const h = k === PK.shard ? s * 0.9 : s * 0.5;
              ctx.moveTo(px + cr * w, py + sr * w);
              ctx.lineTo(px - sr * h, py + cr * h);
              ctx.lineTo(px - cr * w, py - sr * w);
              ctx.lineTo(px + sr * h, py - cr * h);
              ctx.closePath();
              break;
            }
            case PK.puff: {
              const r = s * (1.6 - f * 0.8);
              ctx.moveTo(px + r, py);
              ctx.arc(px, py, r, 0, TAU);
              break;
            }
            case PK.sparkle: {
              const r = s * (0.5 + f * 0.5);
              const t = Math.max(1, r * 0.35);
              ctx.rect(px - t / 2, py - r, t, r * 2);
              ctx.rect(px - r, py - t / 2, r * 2, t);
              break;
            }
          }
          i = next[i]!;
        }
        if (stroked) ctx.stroke(); else ctx.fill();
      }
    }
  }
}
